/**
 * DMMcpAppView — Sandboxed iframe container for rendering MCP App Views.
 *
 * Renders HTML from an MCP App ui:// resource inside a sandboxed iframe,
 * connects via AppBridge + PostMessageTransport, and forwards lifecycle
 * events (tool input, tool result, messages, link opens, resizes).
 */
import { buildAllowAttribute } from '@modelcontextprotocol/ext-apps/app-bridge';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { DMMcpAppViewProps } from './types';
import { useMcpAppBridge } from './useMcpAppBridge';

const DEFAULT_INLINE_HEIGHT = 400;
const MAX_INLINE_HEIGHT = 1200;

const SANDBOX_BASE =
    'allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads';

export function DMMcpAppView({
    html,
    toolArguments,
    toolResult,
    displayMode = 'inline',
    theme = 'light',
    onMessage,
    onModelContextUpdate,
    onToolCall,
    onOpenLink,
    onSizeChange,
    permissions,
    className,
    height,
    width
}: DMMcpAppViewProps) {
    const sentInputRef = useRef(false);
    const [reportedHeight, setReportedHeight] = useState<number>();

    const handleSizeChange = useCallback(
        (reportedWidth?: number, nextHeight?: number) => {
            if (nextHeight && nextHeight > 0) setReportedHeight(nextHeight);
            onSizeChange?.(reportedWidth, nextHeight);
        },
        [onSizeChange]
    );

    const {
        iframeRef,
        isInitialized,
        error,
        sendToolInput,
        sendToolResult,
        setupBridge,
        teardown
    } = useMcpAppBridge({
        onMessage,
        onModelContextUpdate,
        onToolCall,
        onOpenLink,
        onSizeChange: handleSizeChange,
        theme,
        displayMode
    });

    // Once initialized, send tool input and result
    useEffect(() => {
        if (!isInitialized || sentInputRef.current) return;
        sentInputRef.current = true;

        if (toolArguments) {
            sendToolInput(toolArguments);
        }
        if (toolResult) {
            sendToolResult(toolResult);
        }
    }, [
        isInitialized,
        toolArguments,
        toolResult,
        sendToolInput,
        sendToolResult
    ]);

    // Reset sentInputRef when html changes so we re-send to the new iframe
    useEffect(() => {
        sentInputRef.current = false;
    }, [html]);

    // Build iframe allow attribute from permissions
    const allowAttr = permissions ? buildAllowAttribute(permissions) : '';

    // Handle iframe load → kick off bridge setup
    const handleIframeLoad = useCallback(() => {
        setupBridge();
    }, [setupBridge]);

    const [isCtrlHeld, setIsCtrlHeld] = useState(false);
    const [isClickActivated, setIsClickActivated] = useState(false);
    const isScrollPassthrough =
        displayMode === 'inline' && !isCtrlHeld && !isClickActivated;

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Control') setIsCtrlHeld(true);
        };
        const onKeyUp = (event: KeyboardEvent) => {
            if (event.key === 'Control') setIsCtrlHeld(false);
        };
        const onBlur = () => setIsCtrlHeld(false);
        window.addEventListener('keydown', onKeyDown);
        window.addEventListener('keyup', onKeyUp);
        window.addEventListener('blur', onBlur);
        return () => {
            window.removeEventListener('keydown', onKeyDown);
            window.removeEventListener('keyup', onKeyUp);
            window.removeEventListener('blur', onBlur);
        };
    }, []);

    // Determine style
    const iframeStyle: React.CSSProperties = {
        pointerEvents: isScrollPassthrough ? 'none' : 'auto',
        border: 'none',
        width: width ?? '100%',
        height:
            height ??
            Math.min(
                MAX_INLINE_HEIGHT,
                Math.max(DEFAULT_INLINE_HEIGHT, reportedHeight ?? 0)
            ),
        maxHeight: displayMode === 'fullscreen' ? '100vh' : undefined,
        display: 'block'
    };

    return (
        <div
            className={`dm-mcp-app-view ${className ?? ''}`}
            onClick={() => setIsClickActivated(true)}
            onMouseLeave={() => setIsClickActivated(false)}
            style={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: '8px'
            }}
        >
            {/* Error overlay only — the connecting phase is a background PostMessage
                handshake; the iframe already renders beneath it so showing a
                "Connecting…" overlay just causes a visible flash. */}
            {error && (
                <div
                    style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'hsl(var(--muted) / 0.5)',
                        zIndex: 10,
                        fontSize: '14px',
                        color: '#ef4444'
                    }}
                >
                    {`Error: ${error}`}
                </div>
            )}

            {html && (
                <iframe
                    ref={iframeRef as React.LegacyRef<HTMLIFrameElement>}
                    srcDoc={html}
                    sandbox={SANDBOX_BASE}
                    allow={allowAttr}
                    onLoad={handleIframeLoad}
                    style={iframeStyle}
                    title="MCP App View"
                />
            )}
        </div>
    );
}
